import React from "react";
import { CheckCircle2, AlertCircle, AlertTriangle, FileText } from "lucide-react";
import {
  BASELINE_INSPECTION_ITEMS,
  BASELINE_INSPECTION_NOTE,
  FINDINGS_GRADES,
  FINDINGS_GRADES_NOTE,
  QUARTERLY_VISIT_ITEMS,
  QUARTERLY_VISIT_NOTE,
  VISIT_REPORT_ITEMS,
} from "@/data/maintenancePlans";

const GRADE_ICON = { Pass: CheckCircle2, Watch: AlertTriangle, "Act Now": AlertCircle };
const GRADE_STYLES = {
  emerald: { border: "border-emerald-300", bg: "bg-emerald-50", text: "text-emerald-700", chip: "bg-emerald-600" },
  amber: { border: "border-amber-300", bg: "bg-amber-50", text: "text-amber-700", chip: "bg-amber-500" },
  red: { border: "border-red-300", bg: "bg-red-50", text: "text-red-700", chip: "bg-red-600" },
};

function NumberedSectionHeading({ step, eyebrow, title }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <div className="shrink-0 w-9 h-9 rounded-full bg-br-bronze text-white font-bold flex items-center justify-center text-sm">{step}</div>
      <div>
        <div className="br-eyebrow text-br-bronze">{eyebrow}</div>
        <h2 className="br-headline text-2xl sm:text-3xl mt-1">{title}</h2>
      </div>
    </div>
  );
}

function Checklist({ items, twoCol = true, testId }) {
  return (
    <ul className={`grid gap-2 text-sm text-gray-700 ${twoCol ? "sm:grid-cols-2" : ""}`} data-testid={testId}>
      {items.map((item) => (
        <li key={item} className="flex items-start gap-2">
          <CheckCircle2 size={14} className="text-br-bronze mt-0.5 shrink-0" />
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

export default function MaintenanceInspectionSection() {
  return (
    <section className="section-pad bg-br-paper" id="inspection-structure" data-testid="inspection-structure">
      <div className="container-br space-y-10">
        {/* Step 1: Baseline Inspection */}
        <div className="bg-white border border-gray-200 rounded p-6 lg:p-8">
          <NumberedSectionHeading step={1} eyebrow="Inspection Structure" title="Your Initial Baseline Inspection" />
          <p className="text-gray-700 leading-relaxed">
            Before a maintenance membership becomes active, Black Ridge Construction may complete an initial baseline inspection. This visit helps us understand the property, document visible conditions, identify access limitations, and create a home profile for future maintenance visits.
          </p>
          <p className="mt-3 text-gray-700 leading-relaxed">
            This baseline report helps protect both the homeowner and Black Ridge by making the starting condition of the property clear.
          </p>

          <div className="mt-6">
            <div className="font-display tracking-widest uppercase text-sm mb-3">What We Document During the Baseline Visit</div>
            <Checklist items={BASELINE_INSPECTION_ITEMS} testId="baseline-checklist" />
          </div>
          <p className="mt-4 text-xs text-gray-500 italic border-l-2 border-br-bronze pl-3">{BASELINE_INSPECTION_NOTE}</p>
        </div>

        {/* Findings grades */}
        <div className="bg-white border border-gray-200 rounded p-6 lg:p-8">
          <div className="max-w-3xl mb-5">
            <div className="br-eyebrow text-br-bronze">How We Grade Findings</div>
            <h2 className="br-headline text-2xl sm:text-3xl mt-2">Simple Finding Categories: Pass, Watch, Act Now</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            {FINDINGS_GRADES.map((g) => {
              const Icon = GRADE_ICON[g.label] || CheckCircle2;
              const s = GRADE_STYLES[g.tone] || GRADE_STYLES.emerald;
              return (
                <div key={g.label} className={`border-2 ${s.border} ${s.bg} rounded p-4`} data-testid={`grade-${g.label.toLowerCase().replace(/[^a-z0-9]+/g,"-")}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded ${s.chip} text-white text-[10px] uppercase tracking-widest font-bold`}>
                      <Icon size={12} /> {g.label}
                    </span>
                  </div>
                  <p className={`text-sm ${s.text} leading-relaxed`}>{g.body}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-4 text-xs text-gray-500 italic border-l-2 border-br-bronze pl-3">{FINDINGS_GRADES_NOTE}</p>
        </div>

        {/* Quarterly visit checklist */}
        <div className="bg-white border border-gray-200 rounded p-6 lg:p-8">
          <NumberedSectionHeading step={2} eyebrow="Recurring Service" title="What Happens During a Scheduled Maintenance Visit" />
          <p className="text-gray-700 leading-relaxed">
            Each scheduled visit follows a structured checklist so the service is consistent and easy to understand. The exact work performed depends on plan type, access, safety, materials available, property condition, and remaining crew-hour limits.
          </p>
          <div className="mt-5">
            <Checklist items={QUARTERLY_VISIT_ITEMS} testId="quarterly-checklist" />
          </div>
          <p className="mt-4 text-xs text-gray-500 italic border-l-2 border-br-bronze pl-3">{QUARTERLY_VISIT_NOTE}</p>
        </div>

        {/* Visit report */}
        <div className="bg-white border border-gray-200 rounded p-6 lg:p-8">
          <div className="flex items-start gap-3 mb-4">
            <FileText size={28} className="text-br-bronze shrink-0" />
            <div>
              <div className="br-eyebrow text-br-bronze">Your Visit Report</div>
              <h2 className="br-headline text-2xl sm:text-3xl mt-1">What You Receive After a Visit</h2>
            </div>
          </div>
          <p className="text-gray-700 leading-relaxed">
            After a scheduled visit, homeowners receive a clear summary of what was checked, what was completed, what was deferred, what needs to be watched, and what may need an add-on or contractor-service estimate.
          </p>
          <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-700">
            {VISIT_REPORT_ITEMS.map((it) => (
              <div key={it} className="flex items-start gap-2">
                <CheckCircle2 size={14} className="text-br-bronze mt-0.5 shrink-0" />
                <span>{it}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
